import axios from 'axios';
import { config } from './utils/config';

const API_URL = config.API_URL;


console.log(`Using API URL: ${API_URL}`);

// Create an Axios instance with default configuration
export const api = axios.create({
  baseURL: API_URL,  
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});



// Interceptor to add the JWT token to every request if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`; // Attach the token to the Authorization header
  }
  return config;
}, error => {
  return Promise.reject(error);
});


// Add this new function for login
export const loginUser = (identifier, password) => {
  return api.post('/login', { identifier, password })
    .then(response => {
      if (response.status === 200 && response.data.token) {
        return response.data;
      } else {
        throw new Error('Login failed: Invalid credentials.');
      }
    })
    .catch(error => {
      console.error('Login error:', error);
      throw error;
    });
};


// Function to fetch user details
export const fetchUserDetails = () => {
  return api.get('/user/details');
};

//----------------------Family dashboard ----------------------

// Function to fetch dashboard details for a specific family
export const fetchFamilyDashboard = (familyId) => {
  return api.get(`/api/family/${familyId}/dashboard`)
    .then(response => {
      if (!response.data.children) {
        console.error("No children data available in the response");
        throw new Error("No children data available"); // Creating a more specific error if no children data is present
      }
      return response.data;  // Ensure the response data is returned
    })
    .catch(error => {
      console.error("Error fetching dashboard data:", error);
      throw error;  // Rethrow to ensure it can be caught and handled in the component
    });
};

//----------------------Child dashbaod ----------------------

// Function to fetch dashboard details for a specific child
export const fetchChildDashboard = (userId) => {
  return api.get(`/api/child/${userId}/dashboard`)
    .then(response => {
      return response.data;  // Ensure the response data is returned
    })
    .catch(error => {
      console.error("Error fetching child dashboard data:", error);
      throw error;  // Ensure errors are not silently caught
    });
};

//-----------------Transactions-----------------


// Function to fetch recent transactions for a specific account
export const fetchRecentTransactions = (userId, accountId) => {
  console.log(`Fetching transactions for child ID: ${userId}`, "Account ID: ", accountId);
  return api.get(`/users/${userId}/accounts/${accountId}/recent_transactions`)
    .then(response => response.data)
    .catch(error => {
      throw error;
    });
};

//-----------------Allowance Settings-----------------

// Function to fetch allowance settings for a specific child
export const fetchChildAllowanceSettings = (childId) => {
  return api.get(`/api/child/${childId}/allowance_settings`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching allowance settings:", error);
      //console.log(`API--------ChildID: ${childId}`);
      throw error;
    });
};
// Function to update allowance settings for a specific child
export const updateChildAllowanceSettings = (childId, settings) => {
  return api.put(`/api/child/${childId}/allowance_settings`, settings)
    .then(response => {
      console.log("Allowance settings updated:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error updating allowance settings:", error);
      throw error;
    });
};

//-----------------Allowance Splits -----------------

// Function to fetch allowance splits for a child
export const fetchAllowanceSplits = (childId) => {
  return api.get(`/api/child/${childId}/allowance_splits`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching allowance splits:", error);
      throw error;
    });
};


// Function to update allowance splits for a child
export const updateAllowanceSplits = (childId, splits) => {
  return api.put(`/api/child/${childId}/allowance_splits`, splits)
    .then(response => response.data)
    .catch(error => {
      console.error("Error updating allowance splits:", error);
      throw error;
    });
};

// Function to create new allowance splits for a child
export const createAllowanceSplits = (childId, splits) => {
  return api.post(`/api/child/${childId}/allowance_splits`, splits)
    .then(response => response.data)
    .catch(error => {
      console.error("Error creating allowance splits:", error);
      throw error;
    });
};

// Function to delete all allowance splits for a child
export const deleteAllowanceSplits = (childId) => {
  return api.delete(`/api/child/${childId}/allowance_splits`)
    .then(response => response.data)
    .catch(error => {
      console.error("Error deleting allowance splits:", error);
      throw error;
    });
};


//----------------- Accounts -----------------

// Function to fetch all accounts for a specific child
export const fetchChildAccounts = (childId) => {
  return api.get(`/api/child/${childId}/accounts`)
    .then(response => {
      return response.data;  // Ensure the response data is returned
    })
    .catch(error => {
      console.error("Error fetching accounts:", error);
      throw error;  // Rethrow to ensure it can be caught and handled in the component
    });
};

// Function to create a new account for a child
export const createChildAccount = (childId, accountData) => {
  return api.post(`/api/child/${childId}/accounts`, accountData)
    .then(response => response.data)
    .catch(error => {
      console.error("Error creating new account:", error);
      throw error;
    });
};

// Function to update a child's account details
export const updateChildAccount = (childId, accountId, accountData) => {
  return api.put(`/api/child/${childId}/accounts/${accountId}`, accountData)
    .then(response => response.data)
    .catch(error => {
      console.error("Error updating account:", error);
      throw error;
    });
};



// Function to delete a child's account.

export const deleteChildAccount = (childId, accountId) => {
  return api.delete(`/api/child/${childId}/accounts/${accountId}`)
    .then(response => response.data)
    .catch(error => {
      console.error("Error deleting the account:", error);
      throw error;
    });
};


// Deposit fund for Child (splits included)

// Function to deposit funds for a child
export const depositFunds = (childId, amount, reason) => {
  return api.post(`/api/child/${childId}/deposit`, { amount, reason })
    .then(response => response.data)
    .catch(error => {
      console.error('Error during deposit:', error);
      throw error; // Rethrow the error for further handling if needed
    });
};

// Function to perform a transaction (deposit or withdrawal) for a child's account
export const performAccountTransaction = (childId, accountId, amount, reason, transactionType) => {
  if (transactionType !== 'deposit' && transactionType !== 'withdrawal') {
    return Promise.reject(new Error('Invalid transaction type. Must be either "deposit" or "withdrawal".'));
  }

  return api.post(`/api/child/${childId}/accounts/${accountId}/transactions`, {
    amount,
    reason,
    transaction_type: transactionType
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Error during account transaction:', error);
      throw error; // Rethrow the error for further handling if needed
    });
};



/// Function to fetch the total balance for a specific child
export const fetchChildTotalBalance = (childId) => {
  return api.get(`/api/child/${childId}/total_balance`)
    .then(response => {
      return response.data;  // Ensure the response data is returned
    })
    .catch(error => {
      console.error("Error fetching child total balance:", error);
      throw error;  // Rethrow to ensure it can be caught and handled in the component
    });
};


// Function to add a new user
export const addUser = (userData) => {
  return api.post('/api/users', userData)
    .then(response => {
      console.log("User added successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error adding user:", error);
      throw error;
    });
};

// Function to delete a user
export const deleteUser = (userId) => {
  return api.delete(`/api/users/${userId}`)
    .then(response => {
      console.log("User deleted successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error deleting user:", error);
      throw error;
    });
};

//--------------------- FAMILY MEMEBER DETAILS _ GET AND PUT ------------------------

// Function to fetch family member details
export const fetchFamilyMemberDetails = (memberId) => {
  return api.get(`/api/family/member/${memberId}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching family member details:", error);
      throw error;
    });
};

// Function to update family member details
export const updateFamilyMemberDetails = (memberId, updatedDetails) => {
  return api.put(`/api/family/member/${memberId}`, updatedDetails)
    .then(response => {
      console.log("Family member details updated:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error updating family member details:", error);
      throw error;
    });
};

// --------------------   Password reset 

// Function to request a password reset code
export const requestResetCode = (identifier) => {
  return api.post('/request-reset', { identifier })
    .then(response => {
      console.log("Reset code requested successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error requesting reset code:", error);
      throw error.response?.data || error;
    });
};

// Function to reset the password using the reset code
export const resetPassword = (identifier, newPassword, resetCode) => {
  return api.post('/reset-password', { identifier, newPassword, resetCode })
    .then(response => {
      console.log("Password reset successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error resetting password:", error);
      throw error.response?.data || error;
    });
};


// ------------------------ On Boarding 


// Function to verify email
export const verifyEmail = (email) => {
  return api.post('/api/verify-email', { email })
    .then(response => {
      console.log("Email verification initiated:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error verifying email:", error);
      throw error;
    });
};

// Function to verify code
export const verifyCode = (code, email) => {
  return api.post('/api/verify-code', { code, email })
    .then(response => {
      console.log("Code verification successful:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error verifying code:", error);
      throw error;
    });
};

// Function to complete onboarding
export const completeOnboarding = (onboardingData) => {
  return api.post('/api/complete-onboarding', onboardingData)
    .then(response => {
      console.log("Onboarding completed successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error completing onboarding:", error);
      throw error;
    });
};

// Function to update family settings
export const updateFamilySettings = (familyId, settings) => {
  return api.put(`/api/family/${familyId}/settings`, settings)
    .then(response => {
      console.log("Family settings updated successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error updating family settings:", error);
      throw error;
    });
};

// Function to check email existence
export const checkEmailExistence = (email) => {
  return api.get(`/api/check-email-existence`, { params: { email } })
    .then(response => response.data)
    .catch(error => {
      console.error("Error checking email existence:", error);
      throw error;
    });
};

// Function to check username existence
export const checkUsernameExistence = (username) => {
  return api.get(`/api/check-username-existence`, { params: { username } })
    .then(response => response.data)
    .catch(error => {
      console.error("Error checking username existence:", error);
      throw error;
    });
};

// New function to fetch the version number
export const fetchVersionNumber = () => {
  return api.get('/api/version')
    .then(response => {
      return response.data.version;
    })
    .catch(error => {
      console.error("Error fetching version number:", error);
      throw error;
    });
};


// send an invitation for a new family 
export const sendInvitation = (inviteeEmail, inviteeName, senderName) => {
  return api.post('/api/invitations', {
    invitee_email: inviteeEmail,
    invitee_name: inviteeName,
    sender_name: senderName
  })
    .then(response => {
      console.log("Invitation sent successfully:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error sending invitation:", error);
      throw error;
    });
};

// Function to fetch currency comparison for a specific family
export const fetchCurrencyComparison = (familyId) => {
  return api.get(`/api/family/${familyId}/currency-comparison`)
    .then(response => {
      if (!response.data.comparisons || !response.data.familyCurrency) {
        console.error("Invalid currency comparison data in the response");
        throw new Error("Invalid currency comparison data");
      }
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching currency comparison data:", error);
      throw error;
    });
};


// Function to fetch savings interest settings for a child
export const fetchChildSavingsInterestSettings = (childId) => {
  return api.get(`/api/child/${childId}/savings_interest_settings`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching savings interest settings:", error);
      throw error;
    });
};

// Function to update savings interest settings for a child
export const updateChildSavingsInterestSettings = (childId, settings) => {
  return api.put(`/api/child/${childId}/savings_interest_settings`, settings)
    .then(response => {
      console.log("Savings interest settings updated:", response.data);
      return response.data;
    })
    .catch(error => {
      console.error("Error updating savings interest settings:", error);
      throw error;
    });
};

// Function to fetch weekly expenses for a child
export const fetchChildWeeklyExpenses = (childId) => {
  return api.get(`/api/child/${childId}/weekly-expenses`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching weekly expenses:", error);
      throw error;
    });
};

// Function to update weekly expenses for a child
export const updateChildWeeklyExpenses = (childId, expenses) => {
  return api.put(`/api/child/${childId}/weekly-expenses`, expenses)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error updating weekly expenses:", error);
      throw error;
    });
};

// Function to fetch checking account balance sheet for a child
export const fetchCheckingBalanceSheet = (childId) => {
  return api.get(`/api/checking-balance-sheet/${childId}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error("Error fetching checking balance sheet:", error);
      throw error;
    });
};

//Delete family account
export const deleteFamilyAccount = (familyId) => {
  return api.delete(`/api/family/${familyId}`)
    .then(response => response.data)
    .catch(error => {
      console.error("Error deleting the family account:", error);
      throw error;
    });
};